import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { merge } from 'rxjs';
import type { CreateFlagDefinition, FlagDefinition, FlagDefinitionTranslations, UpdateFlagDefinition } from '@zag/feature-flags/common';
import {
  EntityDetailLayoutComponent,
  FormField,
  Input,
  PageActionsDirective,
  ProfileSummaryCardComponent,
  TranslatedInput,
  type ProfileStat,
  ZagButton,
} from '@zag/ui';
import { FeatureToggleGridComponent, type FeatureToggleChange } from '../feature-toggle-grid/feature-toggle-grid.component';

export type FlagDefinitionEditResult =
  | { readonly mode: 'create'; readonly payload: CreateFlagDefinition }
  | { readonly mode: 'update'; readonly key: string; readonly payload: UpdateFlagDefinition };

/**
 * Editor for a feature-flag definition — mirrors the layout of
 * `<zag-licence-definition-edit>` (sidebar profile card + main form
 * area) but drops the `active` field: flag rollout is controlled
 * per-tenant, not on the definition itself.
 */
@Component({
  selector: 'zag-flag-definition-edit',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    EntityDetailLayoutComponent,
    ProfileSummaryCardComponent,
    FeatureToggleGridComponent,
    FormField,
    Input,
    PageActionsDirective,
    TranslatedInput,
    TranslatePipe,
    ZagButton,
  ],
  template: `
    <ng-template zagPageActions>
      <button type="button" zagButton="elevated" zagType="primary" [disabled]="!canSave()" (click)="onSave()">
        {{
          (mode() === 'create' ? 'control.featureFlags.definitions.form.createSubmit' : 'control.featureFlags.definitions.form.save')
            | translate
        }}
      </button>
    </ng-template>

    <zag-entity-detail-layout>
      <ng-container slot="sidebar">
        <zag-profile-summary-card
          [avatarName]="displayName()"
          [avatarIcon]="'flag'"
          [subtitle]="
            (mode() === 'create'
              ? 'control.featureFlags.definitions.sidebar.subtitleCreate'
              : 'control.featureFlags.definitions.sidebar.subtitleUpdate'
            ) | translate
          "
          [stats]="stats()"
        />
      </ng-container>

      <ng-container slot="content">
        <form class="form">
          <section class="section">
            <h2 class="section__title">{{ 'control.featureFlags.definitions.form.general' | translate }}</h2>

            <zag-form-field
              [label]="'control.featureFlags.definitions.form.key' | translate"
              [hint]="
                (mode() === 'create'
                  ? 'control.featureFlags.definitions.form.keyHintCreate'
                  : 'control.featureFlags.definitions.form.keyHintUpdate'
                ) | translate
              "
            >
              <zag-input
                [value]="key()"
                (valueChange)="key.set($event)"
                [readonly]="mode() === 'update'"
                inputClass="mono"
                [placeholder]="'control.featureFlags.definitions.form.keyPlaceholder' | translate"
              />
            </zag-form-field>

            <zag-form-field [label]="'control.featureFlags.definitions.form.name' | translate" [required]="true">
              <zag-translated-input
                fieldKey="name"
                [value]="translations()"
                (valueChange)="translations.set($event)"
                [placeholder]="'control.featureFlags.definitions.form.namePlaceholder' | translate"
              />
            </zag-form-field>

            <zag-form-field [label]="'control.featureFlags.definitions.form.description' | translate">
              <zag-translated-input
                fieldKey="description"
                [value]="translations()"
                (valueChange)="translations.set($event)"
                [multiline]="true"
                [rows]="3"
                [placeholder]="'control.featureFlags.definitions.form.descriptionPlaceholder' | translate"
              />
            </zag-form-field>
          </section>

          <section class="section">
            <header class="section__header">
              <h2 class="section__title">{{ 'control.featureFlags.definitions.form.featuresTitle' | translate }}</h2>
              <span class="section__count">
                {{ 'control.featureFlags.definitions.form.featuresSelected' | translate: { count: selectedCount() } }}
              </span>
            </header>
            <zag-feature-toggle-grid
              [enabledFeatures]="features()"
              [readonly]="false"
              [ariaLabel]="'control.licensing.features.title' | translate"
              (toggled)="onFeatureToggle($event)"
            />
          </section>
        </form>
      </ng-container>
    </zag-entity-detail-layout>
  `,
  styleUrl: './flag-definition-edit.component.scss',
})
export class FlagDefinitionEditComponent {
  private readonly translate = inject(TranslateService);

  readonly flag = input<FlagDefinition | null>(null);

  readonly saved = output<FlagDefinitionEditResult>();

  protected readonly mode = computed<'create' | 'update'>(() => (this.flag() ? 'update' : 'create'));

  protected readonly key = signal('');
  protected readonly translations = signal<FlagDefinitionTranslations>({});
  protected readonly features = signal<readonly string[]>([]);

  protected readonly canonicalName = computed(() => this.firstNonEmpty('name'));
  protected readonly canonicalDescription = computed(() => this.firstNonEmpty('description'));

  private firstNonEmpty(field: 'name' | 'description'): string {
    for (const slot of Object.values(this.translations())) {
      const raw = slot?.[field];
      if (typeof raw === 'string' && raw.trim().length > 0) return raw;
    }
    return '';
  }

  private readonly translationChange = toSignal(
    merge(this.translate.onLangChange, this.translate.onDefaultLangChange, this.translate.onTranslationChange),
    { initialValue: null }
  );

  constructor() {
    effect(() => {
      const current = this.flag();
      if (current) {
        this.key.set(current.key);
        this.translations.set(this.hydrateTranslations(current));
        this.features.set(current.features);
      } else {
        this.key.set('');
        this.translations.set({});
        this.features.set([]);
      }
    });
  }

  private hydrateTranslations(current: FlagDefinition): FlagDefinitionTranslations {
    const merged: FlagDefinitionTranslations = { ...current.translations };
    if (!this.firstNonEmptyIn(merged, 'name') && current.name) {
      merged['en'] = { ...(merged['en'] ?? {}), name: current.name };
    }
    if (!this.firstNonEmptyIn(merged, 'description') && current.description) {
      merged['en'] = { ...(merged['en'] ?? {}), description: current.description };
    }
    return merged;
  }

  private firstNonEmptyIn(map: FlagDefinitionTranslations, field: 'name' | 'description'): string {
    for (const slot of Object.values(map)) {
      const raw = slot?.[field];
      if (typeof raw === 'string' && raw.trim().length > 0) return raw;
    }
    return '';
  }

  protected readonly selectedCount = computed(() => this.features().length);

  protected readonly displayName = computed(() => {
    this.translationChange();
    return this.canonicalName().trim() || this.key() || this.translate.instant('control.featureFlags.definitions.sidebar.defaultName');
  });

  protected readonly stats = computed<ProfileStat[]>(() => {
    this.translationChange();
    return [
      { label: this.translate.instant('control.featureFlags.definitions.sidebar.statKey'), value: this.key() || '—', icon: 'key' },
      {
        label: this.translate.instant('control.featureFlags.definitions.sidebar.statFeatures'),
        value: this.features().length,
        icon: 'list',
      },
    ];
  });

  protected readonly canSave = computed(() => {
    if (!this.canonicalName().trim()) return false;
    if (this.mode() === 'create' && !/^[a-z0-9-]+$/.test(this.key())) return false;
    return true;
  });

  protected onFeatureToggle(change: FeatureToggleChange) {
    const set = new Set(this.features());
    if (change.enabled) set.add(change.feature);
    else set.delete(change.feature);
    this.features.set([...set]);
  }

  protected onSave() {
    if (!this.canSave()) return;
    const translations = this.translations();
    const name = this.canonicalName();
    const description = this.canonicalDescription().trim() || null;
    const features = [...this.features()];
    const current = this.flag();
    if (current) {
      this.saved.emit({
        mode: 'update',
        key: current.key,
        payload: { name, description, features, translations },
      });
    } else {
      this.saved.emit({
        mode: 'create',
        payload: { key: this.key(), name, description, features, translations },
      });
    }
  }
}
